import React from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useCurrentUser } from '../contexts/AuthContext';
import { useGoals, useUpdateGoal, useCreateGoal } from '../hooks/useGoals';
import { GoalCard } from '../components/goals/GoalCard';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ProgressBar } from '../components/ui/ProgressBar';
import { Spinner } from '../components/ui/Spinner';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { 
  Target, 
  TrendingUp, 
  Calendar, 
  Share2, 
  Plus, 
  MoreVertical,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';

export const GoalsPage = () => {
  const { node, getTerm } = useTenant();
  const { can, isAdmin: isGlobalAdmin } = usePermissions();
  const canManageGoals = isGlobalAdmin || can('GOAL_MANAGE') || can('edit_settings');
  const canUpdateGoals = isGlobalAdmin || can('GOAL_UPDATE') || canManageGoals;

  const { data: goals = [], isLoading } = useGoals(node?.id || '');
  const updateMutation = useUpdateGoal(node?.id || '');
  const createMutation = useCreateGoal(node?.id || '');
  
  const [updatingGoal, setUpdatingGoal] = React.useState<any>(null);
  const [isCreating, setIsCreating] = React.useState(false);
  const [newValue, setNewValue] = React.useState('');
  const [note, setNote] = React.useState('');

  const [newGoal, setNewGoal] = React.useState({
    title: '',
    description: '',
    targetValue: '',
    unit: '',
    isPublic: true
  });

  const handleCreateGoal = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      ...newGoal,
      targetValue: parseFloat(newGoal.targetValue),
      currentValue: 0,
      isPublic: newGoal.isPublic
    }, {
      onSuccess: () => {
        toast.success('Goal created');
        setIsCreating(false);
        setNewGoal({ title: '', description: '', targetValue: '', unit: '', isPublic: true });
      },
      onError: () => toast.error('Failed to create goal')
    });
  };

  const handleUpdateProgress = (e: React.FormEvent) => {
    e.preventDefault();
    if (!updatingGoal) return;

    updateMutation.mutate({
      goalId: updatingGoal.id,
      currentValue: parseFloat(newValue),
      ...(note.trim() ? { description: `${updatingGoal.description ? updatingGoal.description + ' — ' : ''}${note.trim()}` } : {})
    }, {
      onSuccess: () => {
        toast.success('Progress updated');
        setUpdatingGoal(null);
        setNewValue('');
        setNote('');
      },
      onError: () => toast.error('Failed to update progress')
    });
  };

  if (isLoading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">Shared Org Goals</h1>
          <p className="text-xs md:text-sm text-gray-500">Track organization objectives and progress collectively.</p>
        </div>
        
        {canManageGoals && (
          <Button variant="primary" onClick={() => setIsCreating(true)}>
            <Plus size={18} />
            New Goal
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {goals.length > 0 ? (
          goals.map(goal => (
            <GoalCard 
              key={goal.id} 
              goal={goal} 
              isAdmin={canUpdateGoals} 
              onUpdate={(g) => {
                setUpdatingGoal(g);
                setNewValue(g.currentValue.toString());
              }} 
            />
          ))
        ) : (
          <Card className="text-center py-20">
            <Target size={40} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">No goals set</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Define objectives to track progress together.</p>
          </Card>
        )}
      </div>

      {isCreating && (
        <Modal
          isOpen={isCreating}
          onClose={() => setIsCreating(false)}
          title="Create New Goal"
          className="max-w-md"
        >
          <form onSubmit={handleCreateGoal} className="space-y-4">
            <Input 
              label="Goal Title"
              value={newGoal.title}
              onChange={(e) => setNewGoal(prev => ({ ...prev, title: e.target.value }))}
              placeholder={`e.g. Total ${getTerm('member')}s by EOY`}
              required
            />
            <Input 
              label="Description (Optional)"
              value={newGoal.description}
              onChange={(e) => setNewGoal(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Why this goal matters..."
            />
            <div className="grid grid-cols-2 gap-4">
              <Input 
                label="Target Value"
                type="number"
                value={newGoal.targetValue}
                onChange={(e) => setNewGoal(prev => ({ ...prev, targetValue: e.target.value }))}
                required
              />
              <Input 
                label="Unit"
                value={newGoal.unit}
                onChange={(e) => setNewGoal(prev => ({ ...prev, unit: e.target.value }))}
                placeholder="e.g. $, members, miles"
                required
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="isPublic" 
                checked={newGoal.isPublic}
                onChange={(e) => setNewGoal(prev => ({ ...prev, isPublic: e.target.checked }))}
                className="rounded border-black dark:border-white text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="isPublic" className="text-xs font-semibold text-gray-700">Make this goal public on your organization home</label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="secondary" className="flex-1" onClick={() => setIsCreating(false)} type="button">
                Cancel
              </Button>
              <Button 
                variant="primary" 
                className="flex-1" 
                type="submit" 
                isLoading={createMutation.isPending}
              >
                Create Goal
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {updatingGoal && (
        <Modal
          isOpen={!!updatingGoal}
          onClose={() => setUpdatingGoal(null)}
          title="Update Progress"
          className="max-w-sm"
        >
          <form onSubmit={handleUpdateProgress} className="space-y-4">
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest font-mono mb-1">Goal</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{updatingGoal.title}</p>
            </div>

            <Input 
              label={`New Value (${updatingGoal.unit})`}
              type="number"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              required
            />

            <div className="space-y-1">
              <label className="block text-xs font-semibold text-gray-700">Note (Optional)</label>
              <textarea 
                className="w-full px-3 py-2 text-sm border-2 border-black dark:border-white rounded-none focus:outline-none focus:border-blue-500"
                rows={3}
                placeholder="What changed?"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="secondary" className="flex-1" onClick={() => setUpdatingGoal(null)} type="button">
                Cancel
              </Button>
              <Button 
                variant="primary" 
                className="flex-1" 
                type="submit" 
                isLoading={updateMutation.isPending}
              >
                Save
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};
